import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Leaf, Mail, Lock, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const intendedFrom = location.state?.from;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, rememberMe);

    if (result.success) {
      // If the user was redirected here from a protected page, send them
      // back to that destination after sign-in. Otherwise fall through to
      // the /login route in App.js, which routes by role/status.
      if (intendedFrom && intendedFrom !== '/login') {
        navigate(intendedFrom, { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } else {
      // Friendly error messages
      if (result.error.includes('user-not-found')) {
        setError('No account found with this email');
      } else if (result.error.includes('wrong-password')) {
        setError('Incorrect password');
      } else if (result.error.includes('invalid-email')) {
        setError('Please enter a valid email');
      } else if (result.error.includes('too-many-requests')) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Unable to sign in. Please try again.');
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <div className="logo-icon">
              <Leaf size={32} />
            </div>
            <div className="logo-text">
              <span className="logo-title">Keel Group</span>
              <span className="logo-subtitle">Operations Portal</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <h2>Welcome back</h2>
          <p>Sign in to manage your operations</p>

          {error && (
            <div className="error-message">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <Mail size={18} />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <Lock size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
              <button 
                type="button" 
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">Remember me for 7 days</span>
            </label>
          </div>

          <button 
            type="submit" 
            className="login-btn"
            disabled={loading}
          >
            {loading ? (
              <span className="spinner" />
            ) : (
              <>
                <LogIn size={18} />
                Sign In
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>Keel Group</p>
          <p>Charlottesville, VA</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
